import React, { useEffect, useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import {
  CheckSquare,
  AlertCircle,
  Clock,
  CheckCircle2,
  Filter,
  ArrowUpDown,
  MoreVertical,
  HelpCircle,
  Plus,
  Trash2,
  X,
  Calendar,
} from 'lucide-react';

const COLUMNS = [
  { id: 'To Do', label: 'To Do', desc: 'Assigned, not started', dotColor: 'bg-white/40' },
  { id: 'In Progress', label: 'In Progress', desc: 'Mentor is working on it', dotColor: 'bg-sky-400' },
  { id: 'On Hold', label: 'On Hold', desc: 'Paused or blocked. Add a reason.', dotColor: 'bg-amber-400' },
  { id: 'In Review', label: 'In Review', desc: 'Submitted, waiting for the admin', dotColor: 'bg-purple-400' },
  { id: 'Done', label: 'Done', desc: 'Complete. Counts in the Overview graph.', dotColor: 'bg-emerald-400' },
];

export default function MentorTasksPage() {
  const [columnsData, setColumnsData] = useState({
    'To Do': [],
    'In Progress': [],
    'On Hold': [],
    'In Review': [],
    'Done': [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [kindFilter, setKindFilter] = useState('All'); // 'All' | 'Task' | 'KRA' | 'KPI'
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'admin' | 'self'
  const [sortBy, setSortBy] = useState('dueDate');

  // Drag state
  const [draggedTaskId, setDraggedTaskId] = useState(null);

  // On Hold Modal
  const [holdModalTask, setHoldModalTask] = useState(null);
  const [holdReason, setHoldReason] = useState('');

  // Create Task Modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newPriority, setNewPriority] = useState('Medium');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await portalApi.getMentorTasks({
        kind: kindFilter,
        sort: sortBy,
        source: sourceFilter,
      });
      setColumnsData(
        res?.columns || {
          'To Do': [],
          'In Progress': [],
          'On Hold': [],
          'In Review': [],
          'Done': [],
        }
      );
    } catch (err) {
      console.error('Failed to load mentor tasks:', err);
      setError(err.message || 'Failed to load mentor tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [kindFilter, sortBy, sourceFilter]);

  const handleCreatePersonalTask = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) {
      setCreateError('Title is required');
      return;
    }
    try {
      setCreating(true);
      setCreateError('');
      await portalApi.createMentorPersonalTask({
        title: newTitle.trim(),
        description: newDescription.trim(),
        dueDate: newDueDate || null,
        priority: newPriority,
      });
      setNewTitle('');
      setNewDescription('');
      setNewDueDate('');
      setNewPriority('Medium');
      setCreateModalOpen(false);
      loadTasks();
    } catch (err) {
      setCreateError(err.message || 'Failed to create personal task');
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePersonalTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this personal task?')) return;
    try {
      await portalApi.deleteMentorPersonalTask(taskId);
      loadTasks();
    } catch (err) {
      alert(err.message || 'Failed to delete personal task');
    }
  };

  const handleStatusChange = async (task, newStatus, reason = '') => {
    if (task.status === newStatus) return;

    // Optimistic UI update
    const previousColumns = { ...columnsData };
    const updatedColumns = { ...columnsData };

    // Remove from old column
    updatedColumns[task.status] = (updatedColumns[task.status] || []).filter((t) => t.id !== task.id);
    // Add to new column
    const updatedTask = { ...task, status: newStatus, statusReason: reason || task.statusReason };
    updatedColumns[newStatus] = [...(updatedColumns[newStatus] || []), updatedTask];

    setColumnsData(updatedColumns);

    try {
      await portalApi.updateMentorTaskStatus(task.id, newStatus, reason);
    } catch (err) {
      console.error('Failed to persist task status:', err);
      setColumnsData(previousColumns); // Rollback
      alert(err.message || 'Failed to persist status change. Rolled back.');
    }
  };

  const handleDragStart = (e, task) => {
    e.dataTransfer.setData('text/plain', task.id);
    setDraggedTaskId(task.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain') || draggedTaskId;
    setDraggedTaskId(null);

    // Find task
    let task = null;
    Object.values(columnsData).forEach((col) => {
      const found = col.find((t) => t.id === taskId);
      if (found) task = found;
    });

    if (!task || task.status === targetColumnId) return;

    if (targetColumnId === 'On Hold') {
      setHoldModalTask(task);
    } else {
      handleStatusChange(task, targetColumnId);
    }
  };

  const formatDueDate = (dateStr, status, completedAt) => {
    if (status === 'Done' && completedAt) {
      return `Done ${new Date(completedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
    }
    if (!dateStr) return 'No due date';
    const dStr = typeof dateStr === 'string' ? dateStr.slice(0, 10) : new Date(dateStr).toISOString().slice(0, 10);
    const [y, m, d] = dStr.split('-').map(Number);
    const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
    return `Due ${dt.toLocaleDateString('en-US', { timeZone: 'UTC', day: 'numeric', month: 'short' })}`;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top Header matching PDF Page 7 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">My Tasks</h1>
          <p className="text-xs text-[#8E8E99] mt-0.5">
            Tasks, KRA and KPI assigned by the Superadmin. Track completion and update status.
          </p>
        </div>

        {/* Filter Pills & Sort Dropdown */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Create Task Button (single icon) */}
          <button
            type="button"
            onClick={() => setCreateModalOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white text-xs font-semibold transition-colors shadow-lg shadow-[#D42B92]/20"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Create Task</span>
          </button>

          {/* Source Filter Pills: All | Admin Assigned | Created by Me */}
          <div className="flex items-center bg-[#131318] p-1 rounded-xl border border-[#2C2C34]">
            {[
              { id: 'all', label: 'All Sources' },
              { id: 'admin', label: 'Admin Assigned' },
              { id: 'self', label: 'Created by Me' },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSourceFilter(s.id)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                  sourceFilter === s.id
                    ? 'bg-[#321129] text-[#F2F2F4] border border-[#D42B92]/40 shadow-sm'
                    : 'text-[#8E8E99] hover:text-[#F2F2F4]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Kind Filter Pills matching PDF Page 7 (All, Task, KRA, KPI) */}
          <div className="flex items-center bg-[#131318] p-1 rounded-xl border border-[#2C2C34]">
            {['All', 'Task', 'KRA', 'KPI'].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKindFilter(k)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  kindFilter === k
                    ? 'bg-[#321129] text-[#F2F2F4] border border-[#D42B92]/40 shadow-sm'
                    : 'text-[#8E8E99] hover:text-[#F2F2F4]'
                }`}
              >
                {k}
              </button>
            ))}
          </div>

          {/* Sort Dropdown */}
          <div className="flex items-center gap-1.5 bg-[#131318] px-3 py-1.5 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99]">
            <ArrowUpDown className="w-3.5 h-3.5" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent text-[#F2F2F4] focus:outline-none cursor-pointer"
            >
              <option value="dueDate" className="bg-[#131318]">Sort: due date</option>
              <option value="createdAt" className="bg-[#131318]">Sort: date assigned</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Distinction Banner matching PDF Page 7 bottom rule */}
      <div className="px-4 py-2.5 rounded-xl bg-white/[0.02] border border-[#2C2C34]/60 text-xs text-[#8E8E99] flex items-center gap-2">
        <HelpCircle className="w-4 h-4 text-[#D42B92] shrink-0" />
        <span>
          <strong>Board scope:</strong> This board contains the mentor's own performance tasks (Admin assigned and personal tasks).
          Tasks created for startups live on each startup's page and in the Calendar.
        </span>
      </div>

      {/* 5-Column Kanban Board matching PDF Page 7 */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 min-h-[550px] overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const colTasks = columnsData[col.id] || [];

          return (
            <div
              key={col.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, col.id)}
              className="bg-[#131318]/70 border border-[#2C2C34] rounded-2xl p-3.5 flex flex-col min-w-[210px] transition-colors"
            >
              {/* Column Header */}
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-[#2C2C34]/80">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${col.dotColor}`} />
                  <span className="text-xs font-bold text-[#F2F2F4]">{col.label}</span>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#08080A] text-[#8E8E99] border border-[#2C2C34]">
                  {colTasks.length}
                </span>
              </div>

              {/* Column Tasks List */}
              <div className="flex-1 space-y-2.5 overflow-y-auto">
                {loading ? (
                  <div className="py-8 text-center text-xs text-[#8E8E99]">Loading…</div>
                ) : colTasks.length === 0 ? (
                  <div className="h-28 border border-dashed border-[#2C2C34]/60 rounded-xl flex items-center justify-center text-center p-3 text-[11px] text-[#8E8E99]/60">
                    Drop items here
                  </div>
                ) : (
                  colTasks.map((task) => (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-[#D42B92]/50 cursor-grab active:cursor-grabbing transition-all space-y-2.5 shadow-sm group"
                    >
                      {/* Title & Target */}
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-xs font-bold text-[#F2F2F4] leading-snug group-hover:text-[#D42B92] transition-colors">
                            {task.title}
                          </div>
                          {(task.canDelete || task.origin === 'MENTOR_SELF') && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePersonalTask(task.id);
                              }}
                              className="text-[#8E8E99] hover:text-red-400 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete personal task"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {task.target && (
                          <div className="text-[11px] text-amber-300 font-medium mt-0.5">
                            Target: {task.target} {task.unit || ''}
                          </div>
                        )}
                        {task.statusReason && task.status === 'On Hold' && (
                          <div className="text-[10px] text-amber-400 italic mt-1 bg-amber-500/10 px-2 py-0.5 rounded">
                            "{task.statusReason}"
                          </div>
                        )}
                      </div>

                      {/* Source badge + Tag & Due Date */}
                      <div className="space-y-1.5 pt-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                              task.origin === 'MENTOR_SELF'
                                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                : 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
                            }`}
                          >
                            {task.origin === 'MENTOR_SELF' ? 'Created by me' : 'Assigned by admin'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-[11px]">
                          <span
                            className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded ${
                              task.kind === 'KRA'
                                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                : task.kind === 'KPI'
                                ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30'
                                : 'bg-white/10 text-white/80 border border-white/20'
                            }`}
                          >
                            {task.kind}
                          </span>
                          <span className="text-[#8E8E99] font-medium">
                            {formatDueDate(task.dueDate, task.status, task.completedAt)}
                          </span>
                        </div>
                      </div>

                      {/* Accessible Status Dropdown on each card */}
                      <div className="pt-2 border-t border-[#2C2C34]/40 flex items-center justify-between">
                        <span className="text-[10px] text-[#8E8E99]">Move to:</span>
                        <select
                          value={task.status}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === 'On Hold') {
                              setHoldModalTask(task);
                            } else {
                              handleStatusChange(task, val);
                            }
                          }}
                          className="bg-[#131318] border border-[#2C2C34] text-[10px] text-[#F2F2F4] rounded-md px-1.5 py-0.5 focus:outline-none focus:border-[#D42B92]"
                        >
                          {COLUMNS.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* On Hold Reason Modal */}
      {holdModalTask && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl w-full max-w-md p-5 space-y-4 shadow-2xl">
            <h3 className="text-sm font-bold text-[#F2F2F4]">Paused or Blocked: Reason Required</h3>
            <p className="text-xs text-[#8E8E99]">
              Please state why "{holdModalTask.title}" is being placed On Hold (e.g. Waiting on admin, pending client data).
            </p>
            <textarea
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              required
              rows={3}
              placeholder="Enter reason..."
              className="w-full px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-xs text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  setHoldModalTask(null);
                  setHoldReason('');
                }}
                className="px-3.5 py-1.5 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99]"
              >
                Cancel
              </button>
              <button
                disabled={!holdReason.trim()}
                onClick={() => {
                  handleStatusChange(holdModalTask, 'On Hold', holdReason.trim());
                  setHoldModalTask(null);
                  setHoldReason('');
                }}
                className="px-4 py-1.5 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-xs text-white font-medium disabled:opacity-50"
              >
                Confirm On Hold
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Personal Task Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl w-full max-w-md p-5 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-[#2C2C34]">
              <h3 className="text-sm font-bold text-[#F2F2F4]">Create Personal Task</h3>
              <button
                type="button"
                onClick={() => {
                  setCreateModalOpen(false);
                  setCreateError('');
                }}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {createError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreatePersonalTask} className="space-y-3.5 text-xs">
              <div>
                <label className="block text-[#8E8E99] font-medium mb-1">Task Title</label>
                <input
                  type="text"
                  required
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Prepare curriculum notes"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                />
              </div>

              <div>
                <label className="block text-[#8E8E99] font-medium mb-1">Description (Optional)</label>
                <textarea
                  rows={3}
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Notes, instructions or checklist..."
                  className="w-full px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[#8E8E99] font-medium mb-1">Due Date</label>
                  <input
                    type="date"
                    value={newDueDate}
                    onChange={(e) => setNewDueDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  />
                </div>
                <div>
                  <label className="block text-[#8E8E99] font-medium mb-1">Priority</label>
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-[#2C2C34]">
                <button
                  type="button"
                  onClick={() => {
                    setCreateModalOpen(false);
                    setCreateError('');
                  }}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white font-medium disabled:opacity-50"
                >
                  {creating ? 'Creating…' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
